/**
 * XML Mapping Service
 * API calls to XML Mappings Lambda
 */

import { apiConfig } from '../config/api';

const BASE_URL = apiConfig.xmlMappingsApi;

export interface FieldMappings {
  sku?: string[];
  size?: string[];
  brand?: string[];
  color?: string[];
  price?: string[];
  title?: string[];
  images?: string[];
  category?: string[];
  condition?: string[];
  description?: string[];
  availability?: string[];
  [key: string]: string[] | undefined;
}

export interface XPathConfig {
  product_root?: string;
  products_container?: string;
  [key: string]: string | undefined;
}

export interface XMLMapping {
  id: string;
  brandId: string;
  mappingName: string;
  fieldMappings: FieldMappings;
  xpathConfig?: XPathConfig;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  brand?: {
    id: string;
    name: string;
    logoUrl?: string;
    websiteUrl?: string;
  };
}

export interface XMLMappingsResponse {
  success: boolean;
  data: {
    mappings: XMLMapping[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  };
  timestamp: string;
}

export interface GetXMLMappingsParams {
  brandId: string;
  limit?: number;
  offset?: number;
}

export interface CreateXMLMappingParams {
  brandId: string;
  mappingName: string;
  fieldMappings: FieldMappings;
  xpathConfig?: XPathConfig;
  isActive?: boolean;
}

export interface UpdateXMLMappingParams {
  mappingName?: string;
  fieldMappings?: FieldMappings;
  xpathConfig?: XPathConfig;
  isActive?: boolean;
}

export class XMLMappingService {
  /**
   * Get XML mappings for a brand
   */
  static async getXMLMappings(params: GetXMLMappingsParams): Promise<XMLMappingsResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('brandId', params.brandId);
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());

      const url = `${BASE_URL}/api/v1/xml-mappings?${queryParams.toString()}`;
      console.log('🔍 Fetching XML mappings:', url);
      
      const response = await fetch(url);
      const data = await response.json();
      
      console.log('✅ XML Mappings Response:', data);
      
      if (!data.success || !data.data) {
        throw new Error('Invalid API response');
      }

      return data;
    } catch (error) {
      console.error('Failed to fetch XML mappings:', error);
      // Return empty response
      return {
        success: false,
        data: {
          mappings: [],
          pagination: {
            total: 0,
            limit: params.limit || 50,
            offset: params.offset || 0,
            hasMore: false,
          },
        },
        timestamp: new Date().toISOString(),
      };
    }
  }

  /**
   * Get XML mappings by brand ID
   */
  static async getMappingsByBrand(brandId: string, isActive?: boolean): Promise<XMLMapping[]> {
    try {
      const queryParams = new URLSearchParams();
      if (isActive !== undefined) queryParams.append('isActive', isActive.toString());

      const url = `${BASE_URL}/api/v1/xml-mappings/brand/${brandId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      console.log('🔍 Fetching mappings by brand:', url);
      
      const response = await fetch(url);
      const data = await response.json();
      
      console.log('✅ Mappings by Brand Response:', data);
      
      if (!data.success || !data.data || !data.data.mappings) {
        throw new Error('Invalid API response');
      }

      return data.data.mappings;
    } catch (error) {
      console.error('Failed to fetch mappings by brand:', error);
      return [];
    }
  }

  /**
   * Get XML mapping by ID
   */
  static async getXMLMappingById(mappingId: string): Promise<XMLMapping | null> {
    try {
      const response = await fetch(`${BASE_URL}/api/v1/xml-mappings/${mappingId}`);
      const data = await response.json();
      
      if (!data.success || !data.data || !data.data.mapping) {
        throw new Error('XML Mapping not found');
      }

      return data.data.mapping;
    } catch (error) {
      console.error('Failed to fetch XML mapping:', error);
      return null;
    }
  }

  /**
   * Create new XML mapping
   */
  static async createXMLMapping(params: CreateXMLMappingParams): Promise<XMLMapping | null> {
    try {
      const response = await fetch(`${BASE_URL}/api/v1/xml-mappings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });
      
      const data = await response.json();
      
      if (!data.success || !data.data || !data.data.mapping) {
        throw new Error('Failed to create XML mapping');
      }

      return data.data.mapping;
    } catch (error) {
      console.error('Failed to create XML mapping:', error);
      return null;
    }
  }

  /**
   * Update XML mapping
   */
  static async updateXMLMapping(
    mappingId: string,
    params: UpdateXMLMappingParams
  ): Promise<XMLMapping | null> {
    try {
      const response = await fetch(`${BASE_URL}/api/v1/xml-mappings/${mappingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });
      
      const data = await response.json();
      
      if (!data.success || !data.data || !data.data.mapping) {
        throw new Error('Failed to update XML mapping');
      }

      return data.data.mapping;
    } catch (error) {
      console.error('Failed to update XML mapping:', error);
      return null;
    }
  }

  /**
   * Delete XML mapping
   */
  static async deleteXMLMapping(mappingId: string): Promise<boolean> {
    try {
      const response = await fetch(`${BASE_URL}/api/v1/xml-mappings/${mappingId}`, {
        method: 'DELETE',
      });
      
      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Failed to delete XML mapping:', error);
      return false;
    }
  }

  /**
   * Activate/Deactivate XML mapping
   */
  static async toggleXMLMapping(mappingId: string, isActive: boolean): Promise<boolean> {
    try {
      const result = await XMLMappingService.updateXMLMapping(mappingId, { isActive });
      return result !== null;
    } catch (error) {
      console.error('Failed to toggle XML mapping:', error);
      return false;
    }
  }
}

